import styled, { css } from 'styled-components';
import Button from '../../../../_shared/components/Button';
import {
  ListVariant,
  TradeContext,
  TradeState,
  TradeViewVariant
} from '../../../../_shared/types/trade';
import applyResponsiveVdpStyles from '../../../../_shared/helpers/applyResponsiveVdpStyles';

/**
 * Variant Definitions
 */
const StyledBidButtonVariants = {
  fr: css`
    font-size: 14px;
  `,
  mini: css`
    min-width: ${({ tradeState }) =>
      tradeState === TradeState.active ? 100 : 270}px;
    padding: 0 14px;
  `,
  expanded: css`
    width: 100%;
  `,
  [TradeViewVariant.LIST]: css`
    height: 36px;
    padding: 0 18px;
    border: none;
    border-right: 3px solid ${({ theme }) => theme.COLORS.BASE};
    border-radius: 6px;
  `,
  [ListVariant.GRID]: css`
    height: 36px;
    border: none;
    padding: 0 16px;
    background-color: ${({ theme }) => theme.COLORS.BASE};
    color: ${({ theme }) => theme.COLORS.TERTIARY};
    font-size: 16px;
    text-align: right;
    z-index: 1;
    &:hover {
      background-color: ${({ theme }) => theme.COLORS.BASE};
    }
  `,
  [TradeViewVariant.VDP]: css`
    ${({ theme }) =>
      applyResponsiveVdpStyles(theme, {
        mini: StyledBidButtonVariants.mini,
        expanded: StyledBidButtonVariants.expanded
      })}
  `
};

const StyledQuickBidButtonVariants = {
  [ListVariant.LIST]: css`
    height: 36px;
    margin-bottom: 0;
  `,
  mini: css`
    padding: 0 7px;
    margin-left: -7px;
  `,
  expanded: css`
    padding: 0 15px;
    margin-left: -6px;
  `
};

const StyledVdpControlVariants = {
  mini: css`
    margin: 0;
    align-items: center;
  `
};

const StyledControlVariants = {
  [TradeState.active]: css`
    align-items: flex-end;
  `,
  [TradeState.upcoming]: css`
    align-items: flex-start;
  `,
  [TradeState.pending]: {
    [TradeContext.Buy]: css`
      margin: 18px;
      border-top: 1px solid ${({ theme }) => theme.COLORS.NEUTRAL_50};
    `,
    [TradeContext.Sell]: css`
      margin: 6px 12px 12px;
      border-top: 0;
    `
  },
  [TradeState.closing]: css`
    margin: 9px 15px;
  `,
  [ListVariant.LIST]: css`
    margin: 0;
  `,
  [ListVariant.GRID]: css`
    margin: 0;
  `,
  [TradeViewVariant.VDP]: css`
    ${({ theme }) =>
      applyResponsiveVdpStyles(theme, {
        mini: StyledVdpControlVariants.mini
      })}
  `
};

const StyledDisabledBidButtonVariants = {
  mini: css`
    ${({ variant }) => variant === TradeViewVariant.VDP && 'min-width: 148px;'}
  `
};

/**
 * Styled Components
 */

export const StyledControls = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: row;
  align-items: flex-end;
  justify-content: space-between;
  height: fit-content;
  margin: 12px 15px;
  ${({ variant }) => StyledControlVariants[variant]};
  ${({ tradeState, tradeContext }) =>
    tradeState === TradeState.pending
      ? StyledControlVariants[tradeState][tradeContext]
      : StyledControlVariants[tradeState]};

  ${({ theme, isOmpStyles }) =>
    isOmpStyles &&
    css`
      align-items: center;

      &:hover .bid-button:not(:hover) {
        color: ${theme.COLORS.NEUTRAL_110};
        background-color: ${theme.COLORS.BASE};
        border-color: ${theme.COLORS.WHITE_LILAC};
      }

      &:hover .bid-button:not(:hover) div {
        color: ${theme.COLORS.NEUTRAL_110};
      }
    `}
`;

export const StyledBidButton = styled(Button.Primary)`
  width: 100%;
  height: 48px;
  font-size: ${({ theme }) => theme.FONT_SIZES.MEDIUM};
  font-family: ${({ theme }) => theme.FONT.SEMI_BOLD};
  border: 3px solid ${({ theme }) => theme.COLORS.BASE};
  border-radius: 9px;
  position: relative;
  box-sizing: content-box;
  padding: 0;
  z-index: 2;

  ${({ locale }) => StyledBidButtonVariants[locale]}
  ${({ variant }) => StyledBidButtonVariants[variant]}

  &:disabled {
    color: ${({ theme }) => theme.COLORS.NEUTRAL_20};
    background: ${({ theme }) => theme.COLORS.BASE};
    border-color: ${({ theme }) => theme.COLORS.NEUTRAL_20};
    cursor: not-allowed;
    ${({ theme }) =>
      applyResponsiveVdpStyles(theme, {
        mini: StyledDisabledBidButtonVariants.mini
      })}
  }

  ${({ theme, isOmpStyles }) =>
    isOmpStyles &&
    css`
      box-sizing: border-box;
      background-color: ${theme.COLORS.WHITE_LILAC};
      border: 2px solid ${theme.COLORS.WHITE_LILAC};
      color: ${theme.COLORS.TERTIARY};
      border-radius: 3px;
      font-size: ${theme.FONT_SIZES.SMALL};
      transition: 0.25s all ease-in-out;

      &:hover {
        background-color: ${theme.COLORS.TERTIARY};
        border: 2px solid ${theme.COLORS.TERTIARY};
        color: ${theme.COLORS.BASE};
      }
    `}
`;

export const StyledQuickBidButton = styled(Button)`
  height: 48px;
  background: ${({ theme }) => theme.COLORS.NEUTRAL_50};
  color: ${({ theme }) => theme.COLORS.BASE_INVERTED};
  font-size: ${({ theme }) => theme.FONT_SIZES.SMALL};
  font-family: ${({ theme }) => theme.FONT.REGULAR};
  border-radius: 0px 6px 6px 0px;
  position: relative;
  z-index: 1;
  white-space: nowrap;
  margin-bottom: 3px;
  text-transform: none;

  ${({ variant }) => StyledQuickBidButtonVariants[variant]};
  ${({ theme, variant }) =>
    applyResponsiveVdpStyles(
      theme,
      {
        mini: StyledQuickBidButtonVariants.mini,
        expanded: StyledQuickBidButtonVariants.expanded
      },
      variant === TradeViewVariant.VDP
    )}

  &:hover {
    background: ${({ theme }) => theme.COLORS.ALTO};
  }

  &:disabled {
    color: ${({ theme }) => theme.COLORS.NEUTRAL_20};
    background: ${({ theme }) => theme.COLORS.BASE};
    border-color: ${({ theme }) => theme.COLORS.NEUTRAL_20};
    cursor: not-allowed;
  }

  ${({ theme, isOmpStyles }) =>
    isOmpStyles &&
    css`
      display: flex;
      align-items: center;
      gap: 6px;
      background-color: ${theme.COLORS.BASE};
      color: ${theme.COLORS.TERTIARY};
      border: 2px solid ${theme.COLORS.WHITE_LILAC};
      border-radius: 3px;
      font-size: ${theme.FONT_SIZES.SMALL};
      margin-bottom: unset;
      padding: 0 12px 0 18px;
      transition: 0.25s all ease-in-out;

      &:hover {
        background-color: ${theme.COLORS.TERTIARY};
        border-color: ${theme.COLORS.TERTIARY};
        color: ${theme.COLORS.YELLOW};
      }

      &:hover div {
        color: ${theme.COLORS.BASE};
      }
    `}
`;

export const StyledQuickBidButtonText = styled.div`
  transition: 0.25s all ease-in-out;
  color: ${({ theme, isOmpStyles }) =>
    isOmpStyles ? theme.COLORS.TERTIARY : theme.COLORS.BASE_INVERTED};
`;

export const NegotiationContainerListView = styled.div`
  display: flex;
  flex-direction: column;
`;
